import { supabase } from '@/integrations/supabase/client';

export interface GameNotification {
  id: string;
  game_id: string;
  recipient_id: string;
  recipient_type: 'supabase' | 'internal';
  notification_type: string;
  message: string;
  is_read: boolean;
  requires_action: boolean;
  action_type?: string;
  sent_at: string;
  expires_at?: string;
}

export const gameNotificationService = {
  // Get user's game notifications (both read and unread)
  async getUserNotifications(userId: string, userType: 'supabase' | 'internal' = 'supabase'): Promise<GameNotification[]> {
    console.log('🎮 NOTIFICATION SERVICE: Fetching notifications for user:', userId, 'type:', userType);
    
    const { data, error } = await supabase
      .from('game_notifications')
      .select('*')
      .eq('recipient_id', userId)
      .eq('recipient_type', userType)
      .order('sent_at', { ascending: false });

    if (error) {
      console.error('🎮 NOTIFICATION SERVICE: Error fetching notifications:', error);
      throw new Error(`Failed to fetch notifications: ${error.message}`);
    }
    
    console.log('🎮 NOTIFICATION SERVICE: Found notifications:', data?.length || 0);
    return (data || []) as GameNotification[];
  },

  // Mark notification as read
  async markAsRead(notificationId: string): Promise<void> {
    const { error } = await supabase
      .from('game_notifications')
      .update({ is_read: true })
      .eq('id', notificationId);

    if (error) throw error;
  },

  // Accept game participation
  async acceptGameParticipation(gameId: string, userId: string, userType: 'supabase' | 'internal' = 'supabase'): Promise<void> {
    console.log('🎮 GAME ACCEPTANCE: Starting for gameId:', gameId, 'userId:', userId, 'userType:', userType);
    
    try {
      // First, get the current game state
      const { data: game, error: gameError } = await supabase
        .from('venue_games')
        .select('home_team_id, away_team_id, home_team_status, away_team_status, game_status, status')
        .eq('id', gameId)
        .single();

      if (gameError) {
        console.error('🎮 GAME ACCEPTANCE: Error fetching game:', gameError);
        throw new Error(`Failed to fetch game data: ${gameError.message}`);
      }

      if (!game) {
        throw new Error('Game not found');
      }

      console.log('🎮 GAME ACCEPTANCE: Current game state:', game);

      // Check if user is a coach/owner of either team (both supabase and internal users)
      let homeTeam = null;
      let awayTeam = null;
      let homeCoaches: any[] = [];
      let awayCoaches: any[] = [];

      if (game.home_team_id) {
        const { data: homeTeamData } = await supabase
          .from('teams')
          .select('created_by')
          .eq('team_id', game.home_team_id)
          .single();
        homeTeam = homeTeamData;

        const { data: homeCoachesData } = await supabase
          .from('team_coaches')
          .select('coach_id')
          .eq('team_id', game.home_team_id);
        homeCoaches = homeCoachesData || [];
      }

      if (game.away_team_id) {
        const { data: awayTeamData } = await supabase
          .from('teams')
          .select('created_by')
          .eq('team_id', game.away_team_id)
          .single();
        awayTeam = awayTeamData;

        const { data: awayCoachesData } = await supabase
          .from('team_coaches')
          .select('coach_id')
          .eq('team_id', game.away_team_id);
        awayCoaches = awayCoachesData || [];
      }

      console.log('🎮 GAME ACCEPTANCE: Team data:', {
        homeTeam: homeTeam?.created_by,
        awayTeam: awayTeam?.created_by,
        homeCoaches: homeCoaches?.map(c => c.coach_id),
        awayCoaches: awayCoaches?.map(c => c.coach_id)
      });

      let updateData: any = {};
      let userCanAccept = false;

      // Check if user can accept for home team
      const isHomeTeamOwner = homeTeam?.created_by === userId;
      const isHomeCoach = homeCoaches?.some(coach => coach.coach_id === userId);
      
      if (isHomeTeamOwner || isHomeCoach) {
        updateData.home_team_status = 'accepted';
        updateData.home_team_accepted_at = new Date().toISOString();
        userCanAccept = true;
        console.log('🎮 GAME ACCEPTANCE: User can accept for HOME team');
      }

      // Check if user can accept for away team
      const isAwayTeamOwner = awayTeam?.created_by === userId;
      const isAwayCoach = awayCoaches?.some(coach => coach.coach_id === userId);
      
      if (isAwayTeamOwner || isAwayCoach) {
        updateData.away_team_status = 'accepted';
        updateData.away_team_accepted_at = new Date().toISOString();
        userCanAccept = true;
        console.log('🎮 GAME ACCEPTANCE: User can accept for AWAY team');
      }

      if (!userCanAccept) {
        console.error('🎮 GAME ACCEPTANCE: User not authorized to accept this game');
        throw new Error('You are not authorized to accept this game. Only team owners and coaches can accept game requests.');
      }

      // Check if game should be confirmed
      const isHomeAccepted = updateData.home_team_status === 'accepted' || game.home_team_status === 'accepted';
      const isAwayAccepted = updateData.away_team_status === 'accepted' || game.away_team_status === 'accepted';
      
      // For single team games (no away team), confirm when home team accepts
      if (!game.away_team_id && isHomeAccepted) {
        updateData.game_status = 'confirmed';
        updateData.game_confirmed_at = new Date().toISOString();
        console.log('🎮 GAME ACCEPTANCE: Single team game confirmed');
      } 
      // For two team games, confirm when both accept
      else if (game.away_team_id && isHomeAccepted && isAwayAccepted) {
        updateData.game_status = 'confirmed';
        updateData.game_confirmed_at = new Date().toISOString();
        console.log('🎮 GAME ACCEPTANCE: Two team game confirmed');
      }

      console.log('🎮 GAME ACCEPTANCE: Update data:', updateData);

      const { error } = await supabase
        .from('venue_games')
        .update(updateData)
        .eq('id', gameId);

      if (error) {
        console.error('🎮 GAME ACCEPTANCE: Error updating game:', error);
        throw new Error(`Failed to update game: ${error.message}`);
      }

      console.log('🎮 GAME ACCEPTANCE: Successfully updated game');

      // Send notifications to venue about team confirmation
      await this.notifyVenueOfTeamConfirmation(gameId, userId, userType, updateData);

      // Send notifications to other stakeholders
      await this.notifyStakeholdersOfGameUpdate(gameId, updateData);

      // Mark the notification as read after successful acceptance
      const { error: notificationError } = await supabase
        .from('game_notifications')
        .update({ is_read: true })
        .eq('game_id', gameId)
        .eq('recipient_id', userId)
        .eq('recipient_type', userType)
        .eq('requires_action', true);

      if (notificationError) {
        console.error('🎮 GAME ACCEPTANCE: Error marking notification as read:', notificationError);
      }
    } catch (error) {
      console.error('🎮 GAME ACCEPTANCE: Failed to accept game:', error);
      throw error;
    }
  },

  // Notify venue about team confirmation
  async notifyVenueOfTeamConfirmation(gameId: string, confirmedByUserId: string, confirmedByUserType: 'supabase' | 'internal', updateData: any): Promise<void> {
    try {
      console.log('🏟️ VENUE NOTIFICATION: Notifying venue of team confirmation');

      // Get game and venue details
      const { data: game, error: gameError } = await supabase
        .from('venue_games')
        .select(`
          *,
          venue_name:venues(venue_name),
          home_team:teams!venue_games_home_team_id_fkey(team_name),
          away_team:teams!venue_games_away_team_id_fkey(team_name)
        `)
        .eq('id', gameId)
        .single();

      if (gameError || !game) {
        console.error('🏟️ VENUE NOTIFICATION: Error getting game details:', gameError);
        return;
      }

      // Determine which team confirmed
      let teamName = 'Team';
      let confirmationType = 'confirmed participation';
      
      if (updateData.home_team_status === 'accepted') {
        teamName = game.home_team?.team_name || 'Home Team';
        confirmationType = 'home team confirmed participation';
      } else if (updateData.away_team_status === 'accepted') {
        teamName = game.away_team?.team_name || 'Away Team';
        confirmationType = 'away team confirmed participation';
      }

      // Check if game is now fully confirmed
      let gameStatus = '';
      if (updateData.game_status === 'confirmed') {
        gameStatus = ' - Game is now confirmed and ready to play!';
      }

      const message = `${teamName} has ${confirmationType} for "${game.game_title}" on ${game.game_date} at ${game.game_time}${gameStatus}`;

      // Send notification to venue (both supabase and internal venue accounts)
      const venueNotifications = [];

      // Check if venue is a supabase user
      const { data: supabaseVenue } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', game.venue_id)
        .maybeSingle();

      if (supabaseVenue) {
        venueNotifications.push({
          recipient_id: game.venue_id,
          recipient_type: 'supabase'
        });
      }

      // Check if venue is an internal user
      const { data: internalVenue } = await supabase
        .from('internal_users')
        .select('id')
        .eq('id', game.venue_id)
        .maybeSingle();

      if (internalVenue) {
        venueNotifications.push({
          recipient_id: game.venue_id,
          recipient_type: 'internal'
        });
      }

      // Send notifications to venue
      for (const venue of venueNotifications) {
        await supabase
          .from('game_notifications')
          .insert({
            game_id: gameId,
            recipient_id: venue.recipient_id,
            recipient_type: venue.recipient_type,
            notification_type: 'team_confirmation',
            message: message,
            requires_action: false,
            action_type: null
          });

        console.log(`✓ Venue notification sent to ${venue.recipient_type} user:`, venue.recipient_id);
      }

    } catch (error) {
      console.error('🏟️ VENUE NOTIFICATION: Error notifying venue:', error);
    }
  },

  // Notify all stakeholders of game updates
  async notifyStakeholdersOfGameUpdate(gameId: string, updateData: any): Promise<void> {
    try {
      console.log('📢 STAKEHOLDER NOTIFICATION: Notifying all stakeholders');

      // Get game details
      const { data: game } = await supabase
        .from('venue_games')
        .select(`
          *,
          home_team:teams!venue_games_home_team_id_fkey(team_name, created_by),
          away_team:teams!venue_games_away_team_id_fkey(team_name, created_by)
        `)
        .eq('id', gameId)
        .single();

      if (!game) return;

      // If game is confirmed, notify scorekeepers
      if (updateData.game_status === 'confirmed') {
        await this.notifyScorekeepersOfGameAvailability(gameId, game);
      }

      // Notify team members if their team confirmed
      if (updateData.home_team_status === 'accepted' && game.home_team_id) {
        await this.notifyTeamMembers(game.home_team_id, gameId, 'Your team has confirmed participation in the game!');
      }

      if (updateData.away_team_status === 'accepted' && game.away_team_id) {
        await this.notifyTeamMembers(game.away_team_id, gameId, 'Your team has confirmed participation in the game!');
      }

    } catch (error) {
      console.error('📢 STAKEHOLDER NOTIFICATION: Error notifying stakeholders:', error);
    }
  },

  // Notify scorekeepers when game becomes available
  async notifyScorekeepersOfGameAvailability(gameId: string, game: any): Promise<void> {
    try {
      if (!game.scorekeeper_needed) return;

      console.log('🏆 SCOREKEEPER NOTIFICATION: Notifying available scorekeepers');

      // Get actual team names for proper notification title
      let homeTeamName = 'Home Team';
      let awayTeamName = 'Away Team';
      
      if (game.home_team_id) {
        const { data: homeTeam } = await supabase
          .from('teams')
          .select('team_name')
          .eq('team_id', game.home_team_id)
          .single();
        if (homeTeam) homeTeamName = homeTeam.team_name;
      }
      
      if (game.away_team_id) {
        const { data: awayTeam } = await supabase
          .from('teams')
          .select('team_name')
          .eq('team_id', game.away_team_id)
          .single();
        if (awayTeam) awayTeamName = awayTeam.team_name;
      }

      // Construct proper title with actual team names
      const gameTitle = game.away_team_id ? `${homeTeamName} vs ${awayTeamName}` : homeTeamName;

      // Get all scorekeepers (both supabase and internal)
      const [supabaseScorekeepers, internalScorekeepers] = await Promise.all([
        supabase
          .from('profiles')
          .select('id, username, first_name, last_name')
          .eq('role', 'scorekeeper'),
        
        supabase
          .from('internal_users')
          .select('id, username')
          .eq('role', 'scorekeeper')
          .eq('is_active', true)
      ]);

      const message = `New scoring opportunity: "${gameTitle}" on ${game.game_date}. Points: ${game.scorekeeper_points || 50}`;

      // Notify supabase scorekeepers
      if (supabaseScorekeepers.data) {
        for (const scorekeeper of supabaseScorekeepers.data) {
          await supabase
            .from('game_notifications')
            .insert({
              game_id: gameId,
              recipient_id: scorekeeper.id,
              recipient_type: 'supabase',
              notification_type: 'scorekeeper_opportunity',
              message: message,
              requires_action: true,
              action_type: 'apply_scorekeeper'
            });
        }
      }

      // Notify internal scorekeepers
      if (internalScorekeepers.data) {
        for (const scorekeeper of internalScorekeepers.data) {
          await supabase
            .from('game_notifications')
            .insert({
              game_id: gameId,
              recipient_id: scorekeeper.id,
              recipient_type: 'internal',
              notification_type: 'scorekeeper_opportunity',
              message: message,
              requires_action: true,
              action_type: 'apply_scorekeeper'
            });
        }
      }

      console.log('✓ Scorekeeper notifications sent');
    } catch (error) {
      console.error('🏆 SCOREKEEPER NOTIFICATION: Error:', error);
    }
  },

  // Notify team members
  async notifyTeamMembers(teamId: number, gameId: string, message: string): Promise<void> {
    try {
      console.log('👥 TEAM NOTIFICATION: Notifying team members');

      // Get team players and coaches
      const [teamPlayers, teamCoaches] = await Promise.all([
        supabase
          .from('team_players')
          .select('user_id')
          .eq('team_id', teamId),
        
        supabase
          .from('team_coaches')
          .select('coach_id')
          .eq('team_id', teamId)
      ]);

      const recipients = [
        ...(teamPlayers.data?.map(p => p.user_id) || []),
        ...(teamCoaches.data?.map(c => c.coach_id) || [])
      ];

      // Send notifications to all team members
      for (const recipientId of recipients) {
        await supabase
          .from('game_notifications')
          .insert({
            game_id: gameId,
            recipient_id: recipientId,
            recipient_type: 'supabase', // Most team members are supabase users
            notification_type: 'team_update',
            message: message,
            requires_action: false,
            action_type: null
          });
      }

      console.log(`✓ Team notifications sent to ${recipients.length} members`);
    } catch (error) {
      console.error('👥 TEAM NOTIFICATION: Error:', error);
    }
  }
};